/**
 * @fileoverview Focus Trap Hook
 * @description Keeps keyboard focus inside an overlay region (drawer, dialog)
 * while it is active, and restores focus to the trigger element on release —
 * required for WCAG 2.4.3 / 2.1.2 on modal overlays (mobile navigation
 * drawer, Drawer)
 */

import { useEffect } from 'react'
import type { RefObject } from 'react'
import { coveringOverlay } from '@/utils/overlayLayers'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

/**
 * Custom hook trapping Tab/Shift+Tab focus cycling within a container
 * @param containerRef - Ref to the element acting as the focus boundary
 * @param active - Trap engages while true; releasing restores prior focus
 */
export const useFocusTrap = (
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  initialFocusSelector?: string
): void => {
  useEffect(() => {
    if (!active) return
    const container = containerRef.current
    if (!container) return

    const previouslyFocused = document.activeElement as HTMLElement | null

    const getFocusable = (): HTMLElement[] =>
      Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((element) => !element.closest('[hidden], [inert]'))

    // A portal confirmation owns focus above its parent panel. The inclusive
    // comparison is the one that answers here: a surface of the container's
    // own rank, opened after it, covers it too.
    const isCovered = () => coveringOverlay(container) !== null

    // Move focus into the trap so the next Tab starts from the overlay,
    // not from the (now background) page content.
    if (!isCovered()) {
      ;(
        (initialFocusSelector
          ? container.querySelector<HTMLElement>(initialFocusSelector)
          : null) ??
        getFocusable()[0] ??
        container
      ).focus()
    }

    // Document-level, capture-phase: the trap must also catch Tab when focus
    // has landed outside the cycle — on the container shell itself (click on
    // non-interactive panel content focuses a tabindex="-1" ancestor) or on
    // an element behind the overlay (aria-modal hides the background from
    // screen readers, so the keyboard must not reach it either).
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || isCovered()) return
      const focusable = getFocusable()
      if (focusable.length === 0) {
        event.preventDefault()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const current = document.activeElement as HTMLElement | null

      if (!current || !focusable.includes(current)) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
      } else if (event.shiftKey && current === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && current === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      // A covering surface can arrive while this trap is being released (for
      // example a URL-backed console inspector replacing mobile navigation).
      // Returning to the old trigger would steal its focus. If inert prevented
      // its first focus attempt, complete the handover now that the background
      // is released.
      const covering = coveringOverlay(container)
      if (covering && !covering.contains(previouslyFocused)) {
        if (!covering.contains(document.activeElement)) {
          ;(
            covering.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? covering
          ).focus()
        }
        return
      }
      if (
        previouslyFocused?.isConnected &&
        !previouslyFocused.closest('[inert]')
      ) {
        previouslyFocused.focus()
      }
    }
  }, [containerRef, active, initialFocusSelector])
}
